export class NDJSONParseError extends Error {
    constructor() {
        super("The stream contained invalid NDJSON");
        this.name = "NDJSONParseError";
    }
}

export async function* parseNDJSONStream<T = unknown>(
    stream: ReadableStream<Uint8Array>,
    options: { strict?: boolean } = {}
): AsyncGenerator<T> {
    const reader = stream.getReader();
    const decoder = new TextDecoder(undefined, { fatal: options.strict });
    let buffer = "";

    const decode = (
        value?: Uint8Array,
        decoderOptions?: TextDecodeOptions
    ): string => {
        try {
            return decoder.decode(value, decoderOptions);
        } catch {
            throw new NDJSONParseError();
        }
    };

    try {
        while (true) {
            const { done, value } = await reader.read();

            if (value) {
                buffer += decode(value, { stream: !done });
            }
            if (done) {
                buffer += decode();
            }
            if (value || done) {
                const lines = buffer.split("\n");

                // Keep the last incomplete line in the buffer
                buffer = lines.pop() || "";

                for (const line of lines) {
                    if (line.trim()) {
                        try {
                            yield JSON.parse(line) as T;
                        } catch (e) {
                            if (options.strict) throw new NDJSONParseError();
                            console.error(
                                "Failed to parse NDJSON line:",
                                line,
                                e
                            );
                        }
                    }
                }
            }

            if (done) break;
        }

        // Process any remaining data in the buffer
        if (buffer.trim()) {
            try {
                yield JSON.parse(buffer) as T;
            } catch (e) {
                if (options.strict) throw new NDJSONParseError();
                console.error("Failed to parse final NDJSON line:", buffer, e);
            }
        }
    } finally {
        reader.releaseLock();
    }
}
